import {
  type SpatialEntityFields,
  type Template,
  type TemplateWithData,
  type RendererResult,
  type TemplateType,
} from '@ekkogmbh/apisdk';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Collapse,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import { Error, ExpandLess, ExpandMore, Warning } from '@mui/icons-material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import { isBase64 } from 'src/Common/formatSupport';

const tryAndExtractJsonFromErrorMessage = (message: string): string => {
  const openBracketIndex = message.indexOf('{');
  const closeBracketIndex = message.lastIndexOf('}');

  if (openBracketIndex < 0 || closeBracketIndex < 0) {
    return message;
  }

  const candidate = message.substring(openBracketIndex, closeBracketIndex + 1);
  try {
    JSON.parse(candidate);
    return candidate;
  } catch (e) {}

  return message;
};

const formatJsonRendererErrorMessages = (message: string): string[] => {
  try {
    const document = JSON.parse(message);
    if (document.events && Array.isArray(document.events)) {
      const messages: string[] = [];
      for (const event of document.events) {
        if (event.message && typeof event.message === 'string') {
          messages.push(event.message);
        }
      }
      // Only return collected messages if there were some. Otherwise we will
      // return the original message string in the end
      if (messages.length > 0) {
        return messages;
      }
    }

    // If our renderer error structure isn't matched simply return the
    // original message.
    return [message];
  } catch (error) {
    // If parsing is impossible, simply return the original
    return [message];
  }
};

interface Props {
  extended?: boolean;
  fields?: SpatialEntityFields;
  preRender?: RendererResult;
  template?: Template | TemplateWithData;
}

export const TemplatePreview = ({ extended, template, fields, preRender }: Props): React.JSX.Element => {
  const { api } = useEkko();
  const renderTimeout = useRef<number>();
  const [isFetching, startFetching] = useRequest();
  const [, startRendering] = useRequest();

  const [data, setData] = useState<string>();
  const [type, setType] = useState<TemplateType>();
  const [rendering, setRendering] = useState<RendererResult>();
  const [isExtended, setExtended] = useState<boolean>(extended === true);

  useEffect(() => {
    if (preRender !== undefined) {
      setRendering(preRender);
    }
  }, [preRender]);

  const renderCallback = useCallback(() => {
    if (preRender !== undefined) {
      // just using the already rendered image if given
      // setRendering(preRender);
      return;
    }

    if (data === undefined || type === undefined) {
      return;
    }

    startRendering(
      () =>
        api.renderTemplate({
          data,
          type,
          fields,
          autoFill: fields === undefined,
          render: true,
        }),
      (result) => setRendering(result as RendererResult),
    );
  }, [data, type, fields, preRender]);

  /** Queue rendering with new data */
  useEffect(() => {
    // handling in a timeout to prevent fetches with quick field changes
    renderTimeout.current = window.setTimeout(renderCallback, 500);
    return () => {
      window.clearTimeout(renderTimeout.current);
    };
  }, [data, type, fields]);

  /** Extract or fetch info required for rendering */
  useEffect(() => {
    if (template === undefined) {
      return;
    }

    setType(template.type);
    if (template.data === undefined) {
      startFetching(
        () => api.getTemplate(template),
        (result) => setData((result as TemplateWithData).data),
      );
    } else {
      setData(template.data);
    }
  }, [template]);

  const [image, errors] = useMemo(() => [rendering?.image, rendering?.errors ?? []], [rendering]);
  const isFailed = useMemo(() => image !== undefined && !isBase64(image), [image]);
  const formattedErrors = useMemo(() => {
    const formattedErrorMessages = [];
    for (const error of errors) {
      const trimmedMessage = tryAndExtractJsonFromErrorMessage(error);
      const formattedMessages = formatJsonRendererErrorMessages(trimmedMessage);
      for (const formattedMessage of formattedMessages) {
        formattedErrorMessages.push(formattedMessage);
      }
    }

    if (image !== undefined && formattedErrorMessages.length > 0) {
      formattedErrorMessages.unshift(image.substring(0, 120) + (image.length > 118 ? '...' : ''));
    }

    return formattedErrorMessages;
  }, [errors, image]);

  const imageElement = useMemo(() => {
    if (image === undefined) {
      if (errors.length === 0) {
        return <Skeleton variant={'rectangular'} animation={isFetching ? 'pulse' : 'wave'} height={200} />;
      } else {
        return <></>;
      }
    }

    if (isFailed) {
      return <Typography sx={{ p: 2 }}>{image}</Typography>;
    }

    return <CardMedia src={`data:image/png;base64,${image}`} component={'img'} />;
  }, [image, errors, isFetching, isFailed]);

  return (
    <Card elevation={3}>
      {imageElement}
      {(errors.length > 0 || isFailed) && (
        <>
          <CardContent style={{ textAlign: 'center' }}>
            <Error color={isFailed ? 'error' : undefined} />
            <Typography>preview not available</Typography>
          </CardContent>
          <Collapse in={errors.length > 0 && isExtended} timeout={'auto'} unmountOnExit>
            <List>
              {formattedErrors.map((message: string, index: number) => (
                <ListItem key={index}>
                  <ListItemIcon>
                    <Warning />
                  </ListItemIcon>
                  <ListItemText primary={message} primaryTypographyProps={{ paragraph: true }} />
                </ListItem>
              ))}
            </List>
          </Collapse>
          <CardActions style={{ justifyContent: 'center' }}>
            <Button
              size="small"
              onClick={() => setExtended(!isExtended)}
              startIcon={extended ? <ExpandLess /> : <ExpandMore />}
              endIcon={extended ? <ExpandLess /> : <ExpandMore />}
            >
              Errors
            </Button>
          </CardActions>
        </>
      )}
    </Card>
  );
};
